import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _post, _put } from "../../../api/apiClient";
import { HttpStatusCode } from "axios";
import store from "../../../app/store";
import { CustomSuccess } from "../../../api/CustomSuccess";
import { CustomError } from "../../../api/CustomError";
import { API_STATUS } from "../sliceUtil";
import {
  ASSET_SIZE_MAX_PLACEHOLDER,
  ASSET_SIZE_MIN_PLACEHOLDER,
  IN_MILLION,
} from "../../../Components/Bank/Cohort/CohortFilter/AssetSize";
import { setLoaderFalse, setLoaderTrue } from "../../../Redux/loaderSlice";
import { COHORT_MODE } from "../../../Components/Bank/Cohort/CohortList/CohortList";
import { cohortRTK } from "./cohortRTK";
import { downloadFHSReportRTK } from "../downloadFHSReport/downloadFHSReportRTK";

export const _idrssd = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.idrssd;
};

export const _cohortReducer = () => {
  const state = store.getState();
  return state?.cohortReducer;
};

export const _fiType = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.fi_Type?.toLowerCase() === "fcu"
    ? "fcu"
    : "bank";
};

const initialState = {
  mode: COHORT_MODE.SUGGESTED,
  isFieldsEditiable: false,
  isEditCohort: false,
  cohortApplied: {
    isLoading: API_STATUS.STATUS_IDLE,
    cohort: null,
  },
  selectedCohort: null,
  clearAll: false,
  selectedInstitutions: [],
  resetSearchTextForCities: false,
  resetSearchTextForAdditionalInstitutions: false,
  selectedAdditionalInstitutions: [],
  assetSize: {
    minValue: null,
    maxValue: null,
  },
  selectedCities: [],
  disableApplyFilter: true,
};

export const applyCohortFilterSlice = createAsyncThunk(
  `apply_cohort_filter`,
  async (_, thunkAPI) => {
    try {
      const config = {
        successMessage: "Applied cohort filter successfully.",
        errorMessage: "Failed to apply cohort filter!",
      };

      const payload = {
        assets: {
          min:
            (_cohortReducer()?.assetSize?.minValue ||
              ASSET_SIZE_MIN_PLACEHOLDER) * IN_MILLION,
          max:
            (_cohortReducer()?.assetSize?.maxValue ||
              ASSET_SIZE_MAX_PLACEHOLDER) * IN_MILLION,
        },
        state: [
          ...(_cohortReducer()?.selectedCities?.map(
            (item) => item.abbreviation,
          ) || []),
        ],
        institution_type: [_fiType()],
      };

      const response = await _post(`/bank/${_idrssd()}/cohort/filter`, payload, {
        metadata: { apiName: "applyCohortFilterSlice" },
      });

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const createCohortSlice = createAsyncThunk(
  `create_cohort`,
  async ({ cohortTitle, cohortDesc }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Cohort created successfully.",
        errorMessage: "Failed to create cohort!",
      };

      const payload = {
        name: cohortTitle,
        description: cohortDesc,
        filterJson: JSON.stringify({
          assets: {
            min:
              (_cohortReducer()?.assetSize?.minValue ||
                ASSET_SIZE_MIN_PLACEHOLDER) * IN_MILLION,
            max:
              (_cohortReducer()?.assetSize?.maxValue ||
                ASSET_SIZE_MAX_PLACEHOLDER) * IN_MILLION,
          },
          state: [
            ...(_cohortReducer()?.selectedCities?.map(
              (item) => item.abbreviation,
            ) ?? []),
          ],
          institution_type: [_fiType()],
          selected: [
            ...(_cohortReducer()?.selectedInstitutions?.map(
              (item) => item.value,
            ) ?? []),
          ],
        }),
      };

      const response = await _post(`/bank/${_idrssd()}/cohort`, payload, {
        metadata: { apiName: "createCohortSlice" },
      });

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      thunkAPI.dispatch(
        cohortRTK.util.invalidateTags([
          "GetCohortDetailsList",
          "GetCohortList",
        ]),
      );
      thunkAPI.dispatch(
        downloadFHSReportRTK.util.invalidateTags(["FHSDataBasedOnQuarter"]),
      );
      thunkAPI.dispatch(setLoaderFalse());
      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const editCohortSlice = createAsyncThunk(
  `edit_cohort`,
  async ({ cohortTitle, cohortDesc, uid, isDefault }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Cohort updated successfully.",
        errorMessage: "Failed to update cohort!",
      };

      let payload = {
        name: cohortTitle,
        description: cohortDesc,
        filterJson: JSON.stringify({
          assets: {
            min:
              (_cohortReducer()?.assetSize?.minValue ||
                ASSET_SIZE_MIN_PLACEHOLDER) * IN_MILLION,
            max:
              (_cohortReducer()?.assetSize?.maxValue ||
                ASSET_SIZE_MAX_PLACEHOLDER) * IN_MILLION,
          },
          state: [
            ...(_cohortReducer()?.selectedCities?.map(
              (item) => item.abbreviation,
            ) ?? []),
          ],
          institution_type: [_fiType()],
          selected: [
            ...(_cohortReducer()?.selectedInstitutions?.map(
              (item) => item.value,
            ) ?? []),
          ],
        }),
      };

      if (isDefault != null) {
        payload = { ...payload, isDefault: !isDefault };
      }

      const response = await _put(`/bank/${_idrssd()}/cohort/${uid}`, payload, {
        metadata: { apiName: "editCohortSlice" },
      });

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const { data, error, message, status, statusCode, responseType } =
        CustomSuccess(response, {
          ...config,
          disableToast: true,
        });

      thunkAPI.dispatch(
        cohortRTK.util.invalidateTags([
          "GetCohortDetailsList",
          "GetCohortList",
        ]),
      );

      thunkAPI.dispatch(
        downloadFHSReportRTK.util.invalidateTags(["FHSDataBasedOnQuarter"]),
      );
      thunkAPI.dispatch(setLoaderFalse());
      return {
        data,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const cohortSlice = createSlice({
  name: "cohort_slice",
  initialState,
  reducers: {
    setMode: (state, action) => {
      state.mode = action.payload;
    },
    setIsFieldsEditiable: (state, action) => {
      state.isFieldsEditiable = action.payload;
    },
    setIsEditCohort: (state, action) => {
      state.isEditCohort = action.payload;
      state.isFieldsEditiable = action.payload;
    },
    setCohortApplied: (state, action) => {
      state.cohortApplied = action.payload;
    },
    setSelectedCohort: (state, action) => {
      state.selectedCohort = action.payload;
    },
    setClearAll: (state, action) => {
      state.clearAll = action.payload;
    },
    setSelectedInstitutions: (state, action) => {
      state.selectedInstitutions = action.payload;
    },
    setResetSearchTextForCities: (state, action) => {
      state.resetSearchTextForCities = action.payload;
    },
    setResetSearchTextForAdditionalInstitutions: (state, action) => {
      state.resetSearchTextForAdditionalInstitutions = action.payload;
    },
    setSelectedAdditionalInstitutions: (state, action) => {
      state.selectedAdditionalInstitutions = action.payload;
    },
    addAddInstitution: (state, action) => {
      const fi = action.payload;
      state.selectedAdditionalInstitutions =
        state.selectedAdditionalInstitutions.filter(
          (i) => i.value !== fi.value,
        );
      if (!state.selectedInstitutions.some((i) => i.value === fi.value)) {
        state.selectedInstitutions.push(fi);
      }
      state.resetSearchTextForAdditionalInstitutions = true;
    },
    removeInstitution: (state, action) => {
      const fi = action.payload;
      state.selectedInstitutions = state.selectedInstitutions.filter(
        (i) => i.value !== fi.value,
      );
      if (
        !state.selectedAdditionalInstitutions.some((i) => i.value === fi.value)
      ) {
        state.selectedAdditionalInstitutions.push(fi);
      }
      state.resetSearchTextForAdditionalInstitutions = true;
    },
    setAssetSize: (state, action) => {
      state.assetSize = action.payload;
    },
    setSelectedCities: (state, action) => {
      state.selectedCities = action.payload;
    },
    setDisableApplyFilter: (state, action) => {
      state.disableApplyFilter = action.payload;
    },

    resetCohort: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(applyCohortFilterSlice.pending, (_state) => ({
        ..._state,
        cohortApplied: {
          ..._state.cohortApplied,
          isLoading: API_STATUS.STATUS_LOADING,
        },
      }))
      .addCase(applyCohortFilterSlice.fulfilled, (_state, _action) => ({
        ..._state,
        cohortApplied: {
          ..._state.cohortApplied,
          cohort: { ..._action?.payload?.data },
          isLoading: API_STATUS.STATUS_SUCCESS,
        },
      }))
      .addCase(applyCohortFilterSlice.rejected, (_state, action) => ({
        ..._state,
        cohortApplied: {
          ..._state.cohortApplied,
          isLoading: API_STATUS.STATUS_FAILURE,
          message: action.error.message || "Error while fetching data",
        },
      }));
  },
});

export const {
  setMode,
  setIsFieldsEditiable,
  setIsEditCohort,
  setCohortApplied,
  setSelectedCohort,
  setClearAll,
  setSelectedInstitutions,
  setResetSearchTextForCities,
  setResetSearchTextForAdditionalInstitutions,
  setSelectedAdditionalInstitutions,
  addAddInstitution,
  removeInstitution,
  setAssetSize,
  setSelectedCities,
  setDisableApplyFilter,
  resetCohort,
} = cohortSlice.actions;

export default cohortSlice.reducer;
